import React, { useContext, useEffect, useState } from 'react';
import {
  Avatar as UnstyledAvatar,
  styled,
  Grid,
  Checkbox,
} from '@nextiva/next-ui-components';
import {
  UserContext,
  actionTypes,
} from 'containers/pages/ManageUsers/usersContext';
import { findLicenseFeature } from 'utils/license-helpers';
import { determineWidth } from 'components/TableHeader';
import { formatStringToDate } from 'utils/formatters';

import RowAction from './RowAction';
import UserStatus from './UserStatus';

const Avatar = styled(UnstyledAvatar)`
  font-size: 12px;
  margin-right: 10px;
`;

export const voiceAction = (link, pageName, user, dispatch) => {
  const { productFeatures, id, nextOS } = user.profiles[0];
  const hasVoiceLicense = !!findLicenseFeature(
    productFeatures,
    'PLATFORM_VOICE_ACCESS',
  );
  const hasVoiceIdent =
    !!nextOS?.primary_identifier || !!nextOS?.primaryIdentifier;
  if (!hasVoiceLicense && !hasVoiceIdent) {
    dispatch({
      type: actionTypes.SET_EDIT_USER,
      user,
    });
    dispatch({
      type: actionTypes.SET_VOICE_SETUP_MODEL,
      voiceSetupModel: {
        open: true,
        userName: `${user.first_name ?? user.firstName} ${
          user.last_name ?? user.lastName
        }`,
        pageName,
      },
    });

    return false;
  }

  const redirectUrl = `${window.location.origin}/apps/voice-admin/#/users/${id}/${link}`;

  window.location.href = redirectUrl;

  return redirectUrl;
};

const UserRow = ({
  onEditUser,
  style,
  user,
  user: {
    profiles,
    first_name,
    firstName,
    last_name,
    lastName,
    status,
    email,
    checked,
  },
}) => {
  const {
    dispatch,
    userState: { dynamicColState, selectedUserList },
  } = useContext(UserContext);

  const [hasChimeIdentity, setHasChimeIdentity] = useState(false);
  const [hasVoiceLicense, setHasVoiceLicense] = useState(false);
  const [hasVoiceIdentity, setHasVoiceIdentity] = useState(false);
  const [isLicensed, setIsLicensed] = useState(false);
  const [inLineStyles, setInLineStyles] = useState({});

  const columns = dynamicColState.map((col, index) => {
    const colField = user[col.fieldState];
    return (
      <Grid
        container
        alignItems="center"
        item
        xs={determineWidth(dynamicColState.length)}
      >
        <div
          className="m-table-row__data"
          data-testid={`PlatformAdminUI-UsersLink-UserRow-${index}`}
        >
          {col.fieldType === 'date' ? formatStringToDate(colField) : colField}
        </div>
      </Grid>
    );
  });

  const locateAccountStatus = () => {
    if (
      profiles[0]?.amazonAccount?.primary_identifier ||
      profiles[0]?.amazonAccount?.primaryIdentifier
    ) {
      setHasChimeIdentity(
        !!profiles[0]?.amazonAccount?.primary_identifier ||
          !!profiles[0]?.amazonAccount?.primaryIdentifier,
      );
    }

    if (profiles[0] && profiles[0].productFeatures) {
      const { productFeatures } = profiles[0];
      const voiceLicense = !!findLicenseFeature(
        productFeatures,
        'PLATFORM_VOICE_ACCESS',
      );

      setHasVoiceLicense(voiceLicense);
    }

    if (
      profiles[0]?.nextOS?.primary_identifier ||
      profiles[0]?.nextOS?.primaryIdentifier
    ) {
      setHasVoiceIdentity(
        !!profiles[0]?.nextOS?.primary_identifier ||
          !!profiles[0]?.nextOS?.primaryIdentifier,
      );
    }
  };

  useEffect(() => {
    locateAccountStatus();

    if (user.profiles[0]?.productFeatures?.length > 0) {
      setIsLicensed(true);
    }
  }, [user]);

  useEffect(() => {
    setInLineStyles(style);
  }, [style]);

  const checkboxHandler = (e) => {
    const userCopy = { ...user, checked: e.target.checked };

    dispatch({
      type: actionTypes.OPTIMISTICALLY_UPDATE_USER,
      user: userCopy,
    });

    if (e.target.checked) {
      dispatch({
        type: actionTypes.SET_TABLE_HEADER_STATE,
        tableHeaderState: true,
      });
    }
    // if the only checked user becomes unchecked
    if (!e.target.checked && selectedUserList.length === 1) {
      dispatch({
        type: actionTypes.SET_TABLE_HEADER_STATE,
        tableHeaderState: false,
      });
    }
  };

  const avatarInitials =
    (first_name ? first_name[0] : firstName[0] ?? '') +
    (last_name ? last_name[0] : lastName[0] ?? '');

  return (
    <>
      {user && (
        <div
          style={inLineStyles}
          className="m-table-row"
          data-testid="PlatformAdminUI-UsersLink-UserRow"
        >
          <Grid container alignItems="center" wrap="nowrap">
            <Grid item>
              <div className="m-table-row__usercheckbox">
                <Checkbox
                  id={email}
                  onChange={checkboxHandler}
                  checked={checked}
                  data-testid="PlatformAdminUI-UsersLink-UserRow-CheckBox"
                />
              </div>
            </Grid>
            <Grid container item>
              <Grid container item xs={3} wrap="nowrap">
                <Avatar
                  size={36}
                  data-testid="PlatformAdminUI-UsersLink-UserRow-Avatar"
                >
                  {avatarInitials}
                </Avatar>
                <div
                  className="m-table-row__info"
                  data-testid="PlatformAdminUI-UsersLink-UserRow-Info"
                >
                  <div>
                    <div
                      className="Body m-table-row__name"
                      data-testid="PlatformAdminUI-UsersLink-UserRow-Name"
                    >
                      {`${first_name ?? firstName} ${last_name ?? lastName}`}
                    </div>
                    <UserStatus status={status} isLicensed={isLicensed} />
                    {hasChimeIdentity && (
                      <span
                        className="chimeIcon"
                        data-testid="PlatformAdminUI-UsersLink-UserRow-ChimeIcon"
                      />
                    )}
                    {hasVoiceIdentity && (
                      <span
                        className="voiceIcon"
                        data-testid="PlatformAdminUI-UsersLink-UserRow-VoiceIcon"
                      />
                    )}
                  </div>
                  <div
                    className="m-table-row__data Caption m-table-row__email"
                    data-testid="PlatformAdminUI-UsersLink-UserRow-Email"
                  >
                    {email}
                  </div>
                </div>
              </Grid>
              {columns}
              <Grid item xs={1} style={{ marginLeft: 'auto' }}>
                <div
                  className="actionBase--selector"
                  data-testid="PlatformAdminUI-UsersLink-UserRow-ActionsButton"
                >
                  <RowAction
                    user={user}
                    hasVoiceIdentity={hasVoiceIdentity}
                    hasVoiceLicense={hasVoiceLicense}
                    hasChimeIdentity={hasChimeIdentity}
                    voiceAction={voiceAction}
                    onEditUser={onEditUser}
                  />
                </div>
              </Grid>
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};

export default UserRow;
